import axios, { AxiosError, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios';

const STUDIO_API_SUFFIX = '/api/studio/v1';
const trimTrailingSlash = (value: string) => value.replace(/\/+$/, '');

const resolveStudioApiBaseUrl = () => {
  const explicit = process.env.REACT_APP_STUDIO_API_URL;
  if (explicit) return trimTrailingSlash(explicit);

  return `http://localhost:4173${STUDIO_API_SUFFIX}`;
};

export const STUDIO_AUTH_TOKEN_KEY = 'kundlistarStudioToken';
export const CURRENT_WORKSPACE_KEY = 'kundlistarStudioCurrentWorkspaceId';
export const STUDIO_API_BASE_URL = resolveStudioApiBaseUrl();

export const studioApi = axios.create({
  baseURL: STUDIO_API_BASE_URL,
  timeout: 30000,
});

axios.defaults.baseURL = STUDIO_API_BASE_URL;

export type StudioApiError = Error & {
  status?: number;
  code?: string;
};

export const getStoredStudioToken = () => localStorage.getItem(STUDIO_AUTH_TOKEN_KEY);

export const getCurrentWorkspaceId = () => localStorage.getItem(CURRENT_WORKSPACE_KEY);

export const setCurrentWorkspaceId = (workspaceId: string | null) => {
  if (workspaceId) {
    localStorage.setItem(CURRENT_WORKSPACE_KEY, workspaceId);
  } else {
    localStorage.removeItem(CURRENT_WORKSPACE_KEY);
  }
};

export const setStoredStudioToken = (token: string | null) => {
  if (token) {
    localStorage.setItem(STUDIO_AUTH_TOKEN_KEY, token);
    studioApi.defaults.headers.common.Authorization = `Bearer ${token}`;
    return;
  }

  localStorage.removeItem(STUDIO_AUTH_TOKEN_KEY);
  delete studioApi.defaults.headers.common.Authorization;
};

const getPlatform = () => {
  const ua = window.navigator.userAgent.toLowerCase();
  if (/iphone|ipad|ipod/.test(ua)) return 'ios';
  if (ua.includes('android')) return 'android';
  return 'web';
};

const applyStudioHeaders = (config: InternalAxiosRequestConfig) => {
  const token = getStoredStudioToken();
  const workspaceId = getCurrentWorkspaceId();

  config.headers = config.headers || {};
  if (token) config.headers.Authorization = `Bearer ${token}`;
  if (workspaceId) config.headers['x-workspace-id'] = workspaceId;
  config.headers['x-platform'] = getPlatform();
  config.headers['x-app-version'] = process.env.REACT_APP_VERSION || 'web';
  config.headers['Accept-Language'] = navigator.language || 'en';

  return config;
};

studioApi.interceptors.request.use(applyStudioHeaders);
axios.interceptors.request.use(applyStudioHeaders);

export const mapStudioApiError = (error: unknown, fallback = 'Request failed'): StudioApiError => {
  const axiosError = error as AxiosError<any>;
  const response = axiosError.response;
  const apiError = new Error(
    response?.data?.message ||
    response?.data?.error ||
    axiosError.message ||
    fallback
  ) as StudioApiError;
  apiError.status = response?.status;
  apiError.code = response?.data?.code;
  return apiError;
};

export const studioRequest = async <T>(config: AxiosRequestConfig, fallback?: string): Promise<T> => {
  try {
    const response = await studioApi.request(config);
    return (response.data?.data ?? response.data) as T;
  } catch (error) {
    throw mapStudioApiError(error, fallback);
  }
};
